import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import MetricCard from "@/components/common/MetricCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#8B5CF6", "#3B82F6", "#06B6D4", "#10B981", "#F59E0B", "#EF4444"];
const DAY_PRESETS = [7, 14, 30, 90];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-strong rounded-lg px-3 py-2 text-xs border border-white/10">
      {label && <div className="text-white/50 mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span>{p.name}:</span>
          <span className="font-medium ml-auto">{p.value?.toLocaleString?.() ?? p.value}</span>
        </div>
      ))}
    </div>
  );
};

function EmptyBlock({ title, hint }) {
  return (
    <div className="h-full min-h-[180px] flex flex-col items-center justify-center text-center gap-1 px-4">
      <Icons.BarChart3 className="w-7 h-7 text-white/25 mb-1" />
      <p className="text-sm text-white/50">{title}</p>
      {hint && <p className="text-xs text-white/35 max-w-xs">{hint}</p>}
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const load = async (n = days) => {
    setLoading(true);
    try {
      const { data: next } = await api.get("/analytics/summary", { params: { days: n } });
      setData(next);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]); // eslint-disable-line

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const { data: result } = await api.post("/analytics/rebuild?purge_demo=true");
      toast.success(
        `Synced live traffic — removed ${result.purged_demo_events || 0} demo events, wrote ${result.rows_written || 0} daily rows`
      );
      await load(days);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const daily = (data?.daily || []).map((d) => ({
    ...d,
    label: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  const withPct = (items) => {
    const total = (items || []).reduce((a, b) => a + (b.value || 0), 0) || 1;
    return (items || []).map((i) => ({ ...i, pct: Math.round((i.value / total) * 100) }));
  };

  const devices = withPct(data?.devices);
  const browsers = withPct(data?.browsers);
  const countries = withPct(data?.countries);
  const trafficSources = withPct(data?.traffic_sources?.map((s) => ({ name: s.source, value: s.value })));
  const totals = data?.totals || {};
  const hasEvents = (totals.events || 0) > 0;

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Actual website traffic, devices, sources, and form leads — not demo numbers."
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="flex items-center gap-1 p-1 rounded-lg glass">
              <Icons.Calendar className="w-3.5 h-3.5 text-white/50 ml-1.5" />
              {DAY_PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDays(n)}
                  className={`h-7 px-2.5 rounded-md text-xs transition-colors ${
                    days === n ? "bg-white/15 text-white" : "text-white/55 hover:text-white hover:bg-white/[0.06]"
                  }`}
                >
                  {n}d
                </button>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              disabled={rebuilding}
              onClick={rebuild}
              className="h-8 text-white/80 border border-white/10"
            >
              <Icons.RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rebuilding ? "animate-spin" : ""}`} />
              {rebuilding ? "Syncing…" : "Sync live data"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {loading && !data ? Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-xl bg-white/[0.03]" />
        )) : (
          <>
            <MetricCard label="Visitors" value={(totals.visitors || 0).toLocaleString()} deltaLabel={`last ${days}d`} icon="Users" accent="#8B5CF6" />
            <MetricCard label="Page views" value={(totals.page_views || 0).toLocaleString()} deltaLabel={`${totals.events || 0} events`} icon="Eye" accent="#3B82F6" />
            <MetricCard label="Leads" value={(totals.leads || 0).toLocaleString()} deltaLabel="from contact form" icon="Inbox" accent="#06B6D4" />
            <MetricCard
              label="Conv. rate"
              value={
                totals.visitors
                  ? `${((100 * (totals.leads || 0)) / totals.visitors).toFixed(1)}%`
                  : "0%"
              }
              deltaLabel="leads / visitors"
              icon="Percent"
              accent="#10B981"
            />
          </>
        )}
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Skeleton className="xl:col-span-2 h-80 rounded-xl bg-white/[0.03]" />
          <Skeleton className="h-80 rounded-xl bg-white/[0.03]" />
        </div>
      ) : (
        <div className={loading ? "opacity-60 pointer-events-none transition-opacity" : ""}>
          {!hasEvents && (
            <GlassCard className="p-4 mb-4 border border-amber-400/20 bg-amber-400/5">
              <div className="flex gap-3 items-start">
                <Icons.Info className="w-4 h-4 text-amber-200 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-50/90">
                  <p className="font-medium">Waiting for live traffic</p>
                  <p className="text-xs text-amber-100/70 mt-1">
                    Browse the public site to generate events, or click <strong>Sync live data</strong> to remove old demo seed rows and rebuild from real hits + leads.
                  </p>
                </div>
              </div>
            </GlassCard>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
            <GlassCard className="xl:col-span-2 p-6">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-white">Visitors & leads</h3>
                <p className="text-xs text-white/40 mt-0.5">Daily unique sessions and form submissions · last {days} days</p>
              </div>
              <div className="h-72">
                {hasEvents || (totals.leads || 0) > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={daily} margin={{ left: -10, right: 0, top: 5, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="visitors" name="Visitors" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="page_views" name="Page views" stroke="#3B82F6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="leads" name="Leads" stroke="#06B6D4" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyBlock title="No traffic yet" hint="Charts fill automatically from the public website tracker." />
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white mb-4">Devices</h3>
              <div className="h-56">
                {devices.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={devices} innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" nameKey="name">
                        {devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyBlock title="No device data" />
                )}
              </div>
              <div className="mt-2 space-y-1.5">
                {devices.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 text-white/70">
                      <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      {d.name}
                    </div>
                    <span className="text-white/60">{d.value.toLocaleString()} · {d.pct}%</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <GlassCard className="p-6 lg:col-span-1">
              <h3 className="text-sm font-medium text-white mb-4">Traffic sources</h3>
              <div className="h-64">
                {trafficSources.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trafficSources}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Views" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyBlock title="No sources yet" />
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white mb-4">Browsers</h3>
              <div className="space-y-3">
                {browsers.length ? browsers.map((b, i) => (
                  <div key={b.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white/80">{b.name}</span>
                      <span className="text-white/50">{b.value.toLocaleString()} · {b.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, b.pct)}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                )) : (
                  <EmptyBlock title="No browser data" />
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="text-sm font-medium text-white mb-4">Top countries</h3>
              <div className="space-y-3">
                {countries.length ? countries.map((c, i) => (
                  <div key={c.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-white/80">{c.name}</span>
                      <span className="text-white/50">{c.value.toLocaleString()} · {c.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.pct)}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                )) : (
                  <EmptyBlock title="No country data" hint="Country needs CDN headers (e.g. Cloudflare CF-IPCountry)." />
                )}
              </div>
            </GlassCard>
          </div>

          <GlassCard className="p-0">
            <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-white">Top pages</h3>
                <p className="text-xs text-white/40 mt-0.5">Most viewed paths on the public site</p>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-white/45 bg-white/[0.02]">
                  <th className="px-6 py-3 font-medium">Path</th>
                  <th className="px-6 py-3 font-medium text-right">Views</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_pages || []).map((p) => (
                  <tr key={p.path} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-6 py-3 text-white font-mono text-xs">{p.path}</td>
                    <td className="px-6 py-3 text-right text-white/70">{(p.views || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {!(data?.top_pages || []).length && (
                  <tr>
                    <td colSpan={2} className="px-6 py-12 text-center text-white/40 text-sm">
                      No page views recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </GlassCard>
        </div>
      )}
    </div>
  );
}
